import type {UserDetails} from 'src/types/referrer-portal';
import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {
  getUserFormInputFields,
  userFormInitialValue,
} from 'src/utils/referrer-portal';
import {
  Button,
  Card,
  CardHeader,
  CardContent,
  CardFooter,
  CardActions,
  SubTitleMedium,
  Input,
  Textarea,
} from '@spaced-out/ui-design-system/lib/components/index';
import UploadProfile from 'src/components/referrer-portal/settings/profile/upload-profile-pic';
import {PhoneInputQuestion} from 'src/components/referrer-portal/user-form/user-form';
import {
  getDefaultPhoneNumber,
  validatePhoneNumber,
} from 'src/components/referrer-portal/settings/profile/setup-profile';
import css from 'src/components/referrer-portal/settings/settings.css';


const EditProfile = ({
  onChangeEdit,
  handleSubmit,
  userData,
  isSubmitting,
}: {
  onChangeEdit: () => unknown;
  handleSubmit: (data: UserDetails) => unknown;
  userData: UserDetails | null | undefined;
  isSubmitting: boolean;
}) => {
  const {t} = useLabelTranslation();
  const USER_FORM_INPUT_FIELDS = getUserFormInputFields(t);
  const [formData, setFormData] = React.useState(
    userData ?? userFormInitialValue,
  );
  const [errors, setErrors] = React.useState(new Map());

  const requiredFields = ['name', 'email'];

  const validate = () => {
    const newErrors = new Map();
    requiredFields.forEach((field) => {
      // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'UserDetails'.
      const value = formData[field];
      if (!value) {
        newErrors.set(field, t('Field_is_required', 'Field is required'));
      }
    });
    if (!validatePhoneNumber(formData.phone_number)) {
      newErrors.set(
        'phone_number',
        `${t(
          'Please_provide_a_valid_phone_number',
          'Please provide a valid phone number',
        )}.`,
      );
    }
    setErrors(newErrors);
    return newErrors;
  };

  const isFieldRequired = (field: string) => requiredFields.includes(field);

  const handleChange = (fieldName: string, value: string) => {
    // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'UserDetails'.
    formData[fieldName] = value;
    setFormData({...formData});
  };

  const onSubmit = () => {
    const error = validate();
    if (error.size > 0) {
      return;
    }

    const contact =
      // here number foramt is comming in array with country code and number
      getDefaultPhoneNumber(formData.phone_number).filter((item) => item)
        .length === 2
        ? formData.phone_number
        : '';
    const payload = {...formData, phone_number: contact} as const;
    // @ts-expect-error - TS2704 - The operand of a 'delete' operator cannot be a read-only property.
    delete payload.id;
    // @ts-expect-error - TS2704 - The operand of a 'delete' operator cannot be a read-only property.
    delete payload.email;
    handleSubmit(payload);
  };

  return (
    <Card classNames={{wrapper: css.profileCard}}>
      <CardHeader>
        <SubTitleMedium>
          {t('Personal_Information', 'Personal Information')}
        </SubTitleMedium>
        <UploadProfile isEdit={true} />
      </CardHeader>
      <CardContent className={css.flexColumn}>
        <div className={css.detailsRow}>
          {USER_FORM_INPUT_FIELDS.map((element) => (
            <div className={css.flexColumn}>
              {element.key === 'phone_number' ? (
                <PhoneInputQuestion
                  value={getDefaultPhoneNumber(formData[element.key])}
                  onChange={(value) =>
                    handleChange(element.key, value.join(''))
                  }
                  error={errors.has(element.key)}
                />
              ) : (
                <Input
                  key={element.key}
                  // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'UserDetails'.
                  value={formData[element.key]}
                  onChange={(event) => {
                    handleChange(element.key, event.target.value);
                  }}
                  label={element.label}
                  error={errors.has(element.key)}
                  errorText={errors.get(element.key)}
                  required={isFieldRequired(element.key)}
                  disabled={element.key === 'email'}
                />
              )}
            </div>
          ))}
        </div>
        <div className={css.defaultMarginTop}>
          <Textarea
            label={t('Bio', 'Bio')}
            value={formData['bio']}
            onChange={(event) => handleChange('bio', event.target.value)}
          />
        </div>
      </CardContent>
      <CardFooter>
        <CardActions>
          <Button type="ghost" onClick={onChangeEdit}>
            {t('Cancel', 'Cancel')}
          </Button>
          <Button isLoading={isSubmitting} onClick={onSubmit}>
            {t('Save_Changes', 'Save Changes')}
          </Button>
        </CardActions>
      </CardFooter>
    </Card>
  );
};

export default EditProfile;
